import * as THREE from "three";
import { CAMERA } from "./config";

const NOTCHES = 11;

export class IsoCamera {
  readonly camera: THREE.OrthographicCamera;

  yaw: number = CAMERA.azimuth;
  frustum: number = THREE.MathUtils.clamp(CAMERA.frustumHeight, CAMERA.minFrustum, CAMERA.maxFrustum);

  private frustumTarget = this.frustum;
  private frustumVelocity = 0;
  private readonly focus = new THREE.Vector3();
  private readonly desiredFocus = new THREE.Vector3();
  private aspect = 1;

  constructor() {
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 600);
  }

  /** aim the spring at a frustum: the camera eases there rather than jumping */
  zoomTo(frustum: number): void {
    this.frustumTarget = THREE.MathUtils.clamp(
      frustum,
      CAMERA.minFrustum,
      CAMERA.maxFrustum,
    );
  }

  /** set the resting zoom directly — for initial values and dev params, no spring */
  setFrustum(value: number): void {
    const clamped = THREE.MathUtils.clamp(value, CAMERA.minFrustum, CAMERA.maxFrustum);
    this.frustum = clamped;
    this.frustumTarget = clamped;
    this.frustumVelocity = 0;
    this.apply();
  }

  jumpTo(position: THREE.Vector3): void {
    this.desiredFocus.set(position.x, position.y + CAMERA.lookHeight, position.z);
    this.focus.copy(this.desiredFocus);
    this.apply();
  }

  follow(position: THREE.Vector3): void {
    this.desiredFocus.set(position.x, position.y + CAMERA.lookHeight, position.z);
  }

  zoom(factor: number): void {
    this.frustumTarget = THREE.MathUtils.clamp(
      this.frustumTarget * factor,
      CAMERA.minFrustum,
      CAMERA.maxFrustum,
    );
  }

  get notch(): number {
    const span = Math.log(CAMERA.maxFrustum / CAMERA.minFrustum);
    const index = (Math.log(this.frustumTarget / CAMERA.minFrustum) / span) * (NOTCHES - 1);
    return Math.round(index);
  }

  stepZoom(direction: number): void {
    const next = THREE.MathUtils.clamp(this.notch + direction, 0, NOTCHES - 1);
    const span = Math.log(CAMERA.maxFrustum / CAMERA.minFrustum);
    this.frustumTarget = CAMERA.minFrustum * Math.exp((next / (NOTCHES - 1)) * span);
  }

  orbit(delta: number): void {
    this.yaw += delta;
  }

  setAspect(aspect: number): void {
    this.aspect = Math.max(0.1, aspect);
    this.apply();
  }

  update(delta: number): void {
    const alpha = 1 - Math.exp(-delta / CAMERA.damping);
    this.focus.lerp(this.desiredFocus, alpha);

    // slightly underdamped so a notch change settles with a touch of spring
    const step = Math.min(delta, 0.05);
    this.frustumVelocity += (this.frustumTarget - this.frustum) * 92 * step;
    this.frustumVelocity *= Math.exp(-15 * step);
    this.frustum += this.frustumVelocity * step;

    if (Math.abs(this.frustumTarget - this.frustum) < 0.002 && Math.abs(this.frustumVelocity) < 0.002) {
      this.frustum = this.frustumTarget;
      this.frustumVelocity = 0;
    }
    this.apply();
  }

  get focusPoint(): THREE.Vector3 {
    return this.focus;
  }

  private apply(): void {
    const azimuth = THREE.MathUtils.degToRad(this.yaw);
    const elevation = THREE.MathUtils.degToRad(CAMERA.elevation);
    const direction = new THREE.Vector3(
      Math.cos(elevation) * Math.sin(azimuth),
      Math.sin(elevation),
      Math.cos(elevation) * Math.cos(azimuth),
    ).normalize();

    const halfHeight = this.frustum / 2;
    const halfWidth = halfHeight * this.aspect;
    this.camera.left = -halfWidth;
    this.camera.right = halfWidth;
    this.camera.top = halfHeight;
    this.camera.bottom = -halfHeight;
    this.camera.position.copy(this.focus).addScaledVector(direction, CAMERA.distance);
    this.camera.lookAt(this.focus);
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();
  }
}
